// Phase C — Public free repair cost estimator (SEO lead-gen tool)
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Wrench, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CATEGORY_FALLBACK = [
  { code: "phone", name: "Phone" },
  { code: "laptop", name: "Laptop" },
  { code: "tablet", name: "Tablet" },
  { code: "smartwatch", name: "Smartwatch" },
  { code: "headphones", name: "Headphones" },
  { code: "camera", name: "Camera" },
  { code: "gaming", name: "Gaming Console" },
  { code: "tv", name: "TV / Monitor" },
  { code: "other", name: "Other" },
];

const CATEGORY_EXAMPLES = {
  phone: "iPhone 15 Pro",
  laptop: "MacBook Pro 14",
  tablet: "iPad Pro 13",
  smartwatch: "Apple Watch Series 9",
  headphones: "AirPods Pro 2",
  camera: "Sony A7 IV",
  gaming: "PlayStation 5",
  tv: "LG C3 OLED",
  other: "",
};

export default function RepairCostEstimator() {
  const nav = useNavigate();
  const [categories, setCategories] = useState(CATEGORY_FALLBACK);
  const [gadgetCategory, setGadgetCategory] = useState("phone");
  const [issues, setIssues] = useState([]);
  const [phoneModel, setPhoneModel] = useState("iPhone 15 Pro");
  const [issueCode, setIssueCode] = useState("screen");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/masters/lookup/gadget_category")
      .then((r) => {
        const rows = (r.data || [])
          .map((row) => ({ code: row.code || row.data?.code, name: row.name || row.data?.name }))
          .filter((c) => c.code);
        if (rows.length) setCategories(rows);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPhoneModel(CATEGORY_EXAMPLES[gadgetCategory] || "");
  }, [gadgetCategory]);

  useEffect(() => {
    // Public masters lookup — issue_type is exposed to any authed user; we need it public.
    // Fallback list keeps the tool useful even if the API blocks unauth reads.
    api.get("/masters/lookup/issue_type")
      .then((r) => setIssues(r.data || []))
      .catch(() => setIssues([
        { code: "screen", name: "Cracked Screen" },
        { code: "water", name: "Water Damage" },
        { code: "battery", name: "Battery Failure" },
        { code: "camera", name: "Camera Fault" },
        { code: "audio", name: "Speaker / Mic" },
        { code: "charging", name: "Charging Port" },
      ]));
  }, []);

  const estimate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/repair-cost-estimate", {
        issue_code: issueCode,
        phone_model: phoneModel || null,
        gadget_category: gadgetCategory,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Estimate failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="repair-cost-page">
      <header className="max-w-4xl mx-auto px-6 pt-8">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <div className="font-display font-semibold text-lg">Treasurescare</div>
            <div className="text-[10px] overline text-muted-foreground">Free repair estimator</div>
          </div>
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center">
          <div className="overline text-secondary flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3" /> Free — no signup
          </div>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-light tracking-tighter">
            How much will your <span className="text-primary italic">gadget repair</span> cost?
          </h1>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            Real UK repair prices for phones, laptops, tablets, watches — every gadget.
            See what you'd pay out-of-pocket versus a year of Treasurescare cover.
          </p>
        </div>

        <div className="mt-10 bg-card border border-border rounded-3xl p-8 shadow-teal-glow">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-muted-foreground">Gadget type</label>
              <Select value={gadgetCategory} onValueChange={setGadgetCategory}>
                <SelectTrigger className="mt-1" data-testid="estimator-gadget-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Model</label>
              <Input
                value={phoneModel}
                onChange={(e) => setPhoneModel(e.target.value)}
                placeholder="e.g. iPhone 15 Pro"
                className="mt-1"
                data-testid="estimator-model-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">What's wrong?</label>
              <Select value={issueCode} onValueChange={setIssueCode}>
                <SelectTrigger className="mt-1" data-testid="estimator-issue-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {issues.map((i) => (
                    <SelectItem key={i.code || i.id || i.name} value={(i.code || i.name || "").toLowerCase()}>
                      {i.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                className="w-full bg-primary text-primary-foreground"
                onClick={estimate}
                disabled={loading}
                data-testid="estimator-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>Get estimate <ArrowRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>

          {result && (
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="estimator-result">
              <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
                <div className="overline text-destructive">If you pay out of pocket</div>
                <div className="mt-2 font-display text-5xl font-semibold" data-testid="estimator-out-of-pocket">
                  £{result.estimate_gbp.toFixed(2)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{result.issue} · one-off repair</div>
              </div>
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
                <div className="overline text-primary">With Treasurescare Hunter</div>
                <div className="mt-2 font-display text-5xl font-semibold text-primary" data-testid="estimator-with-cover">
                  £{result.annual_cover_cost_gbp.toFixed(2)}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">1 full year — up to 4 claims covered</div>
                {result.net_saving_gbp > 0 && (
                  <div className="mt-4 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                    <Sparkles className="w-3 h-3" /> Save £{result.net_saving_gbp.toFixed(0)} · {Math.round((result.net_saving_gbp / result.estimate_gbp) * 100)}%
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 items-center justify-between mt-2">
                <div className="text-sm text-muted-foreground">
                  <Wrench className="w-4 h-4 inline mr-1 text-secondary" />
                  {result.message}
                </div>
                <Button
                  onClick={() => nav("/signup")}
                  className="bg-secondary text-teal hover:bg-secondary/90"
                  data-testid="estimator-signup-cta"
                >
                  Get protected <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground underline">
            Back to Treasurescare
          </Link>
        </div>
      </main>
    </div>
  );
}
